import { RISK_STATUS_LABELS } from '@/pages/risk/RiskBadges'
import { Shield } from 'lucide-react'
import {
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  formatToolValue,
  getToolCallId,
  ToolDetailOverlayLink,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const completedIcon = (
  <Shield className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
)

function formatRiskMutationDetail(
  state: ReturnType<typeof readToolState>,
  fields: string[]
): string | null {
  const values = fields
    .map((field) => {
      const value = state.result?.[field] ?? state.args?.[field]
      return formatToolValue(field === 'status' ? RISK_STATUS_LABELS[String(value)] ?? value : value)
    })
    .filter((value): value is string => Boolean(value))

  return values.length > 0 ? values.join(' · ') : null
}

function formatChangedFields(state: ReturnType<typeof readToolState>) {
  const fields = Array.isArray(state.result?.changedFields)
    ? state.result.changedFields
    : []
  const changedFields = fields
    .map((field) => formatToolValue(field))
    .filter((field): field is string => Boolean(field))

  return changedFields.length > 0
    ? changedFields.join(', ')
    : pickString(state.result?.riskId, state.args?.riskId)
}

function RiskMutationToolMessage({
  part,
  loadingLabel,
  completedLabel,
  detailFields,
}: ToolPartProps & {
  loadingLabel: string
  completedLabel: string
  detailFields: string[]
}) {
  const state = readToolState(part)
  const failed = state.isError || isToolError(state)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel={loadingLabel}
      completedLabel={completedLabel}
      loadingDetail={pickString(state.args?.riskId)}
      completedDetail={
        failed ? formatRiskMutationDetail(state, detailFields) : null
      }
      completedIcon={completedIcon}
    />
  )
}

export function RisksListToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const count =
    typeof state.result?.count === 'number' ? state.result.count : null
  const risks: Array<Record<string, unknown>> = Array.isArray(
    state.result?.risks
  )
    ? state.result.risks
    : []
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading risks...'
        completedLabel={
          count !== null ? `${plural(count, 'risk')} loaded` : 'Risks loaded'
        }
        completedDetail={pickString(
          state.args?.riskRating,
          RISK_STATUS_LABELS[String(state.args?.status)] ?? state.args?.status,
          state.args?.category,
          state.args?.ownerLabel
        )}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No risks found'
          items={risks.map((risk) => ({
            id: pickString(risk.displayId),
            title: pickString(risk.title) ?? 'Untitled risk',
            subtitle: pickString(risk.affectedSystem, risk.ownerLabel),
            meta: [risk.riskRating, RISK_STATUS_LABELS[String(risk.status)] ?? risk.status, risk.category],
            tone: normalizeTone(risk.riskRating),
          }))}
        />
      ) : null}
    </>
  )
}

export function RisksGetToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading risk details...'
        completedLabel='Risk details loaded'
        loadingDetail={pickString(state.args?.riskId)}
        completedDetail={pickString(
          state.result?.displayId,
          state.args?.riskId
        )}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) && state.result ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(
            part,
            `risk-${pickString(state.result.displayId, state.args?.riskId) ?? 'detail'}`
          )}
          systemDetail={
            pickString(state.result.id)
              ? {
                  entityType: 'risk',
                  entityId: pickString(state.result.id)!,
                  workspaceId: '',
                }
              : null
          }
          payload={{
            entityType: 'risk',
            toolName: 'risksGet',
            title: pickString(state.result.title) ?? 'Untitled risk',
            subtitle: pickString(state.result.displayId, state.args?.riskId),
            description: pickString(state.result.riskStatement),
            fields: [
              {
                label: 'Rating',
                value: formatToolValue(state.result.riskRating),
                tone: normalizeTone(state.result.riskRating),
              },
              { label: 'Type', value: formatToolValue(state.result.riskType) },
              {
                label: 'Category',
                value: formatToolValue(state.result.category),
              },
              { label: 'Status', value: formatToolValue(RISK_STATUS_LABELS[String(state.result.status)] ?? state.result.status) },
              {
                label: 'Treatment',
                value: formatToolValue(state.result.treatment),
              },
              {
                label: 'Likelihood',
                value: formatToolValue(state.result.likelihood),
              },
              { label: 'Impact', value: formatToolValue(state.result.impact) },
              {
                label: 'Owner',
                value: formatToolValue(state.result.ownerLabel),
              },
              {
                label: 'Target date',
                value: formatToolValue(state.result.targetDate),
              },
              {
                label: 'Review date',
                value: formatToolValue(state.result.reviewDate),
              },
            ],
            sections: [
              {
                title: 'Mitigation plan',
                content: formatToolValue(state.result.mitigationPlan),
              },
            ],
          }}
        />
      ) : null}
    </>
  )
}

export function RisksSearchToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const count =
    typeof state.result?.count === 'number' ? state.result.count : null
  const risks: Array<Record<string, unknown>> = Array.isArray(
    state.result?.risks
  )
    ? state.result.risks
    : []
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Searching risks...'
        completedLabel={
          count !== null
            ? `Found ${plural(count, 'risk')}`
            : 'Risk search complete'
        }
        loadingDetail={pickString(state.args?.query)}
        completedDetail={count === 0 ? null : pickString(state.args?.query)}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No matching risks'
          items={risks.map((risk) => ({
            id: pickString(risk.displayId),
            title: pickString(risk.title) ?? 'Untitled risk',
            subtitle: pickString(risk.affectedSystem, risk.ownerLabel),
            meta: [risk.riskRating, RISK_STATUS_LABELS[String(risk.status)] ?? risk.status, risk.category],
            tone: normalizeTone(risk.riskRating),
          }))}
        />
      ) : null}
    </>
  )
}

export function RisksStatsToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const total =
    typeof state.result?.total === 'number' ? state.result.total : null
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Summarizing risk register...'
      completedLabel={
        total !== null
          ? `${plural(total, 'risk')} in register`
          : 'Risk register summary loaded'
      }
      completedIcon={completedIcon}
    />
  )
}

export function RisksLinkControlToolMessage({ part }: ToolPartProps) {
  return (
    <RiskMutationToolMessage
      part={part}
      loadingLabel='Linking control to risk...'
      completedLabel='Control linked to risk'
      detailFields={['riskId', 'controlDefId', 'linkType']}
    />
  )
}

export function RisksUnlinkControlToolMessage({ part }: ToolPartProps) {
  return (
    <RiskMutationToolMessage
      part={part}
      loadingLabel='Unlinking control from risk...'
      completedLabel='Control unlinked from risk'
      detailFields={['riskId', 'controlDefId']}
    />
  )
}

export function RisksLinkAssetToolMessage({ part }: ToolPartProps) {
  return (
    <RiskMutationToolMessage
      part={part}
      loadingLabel='Linking asset to risk...'
      completedLabel='Asset linked to risk'
      detailFields={['riskId', 'assetId']}
    />
  )
}

export function RisksUnlinkAssetToolMessage({ part }: ToolPartProps) {
  return (
    <RiskMutationToolMessage
      part={part}
      loadingLabel='Unlinking asset from risk...'
      completedLabel='Asset unlinked from risk'
      detailFields={['riskId', 'assetId']}
    />
  )
}

export function RisksLinkVendorToolMessage({ part }: ToolPartProps) {
  return (
    <RiskMutationToolMessage
      part={part}
      loadingLabel='Linking vendor to risk...'
      completedLabel='Vendor linked to risk'
      detailFields={['riskId', 'vendorId']}
    />
  )
}

export function RisksUnlinkVendorToolMessage({ part }: ToolPartProps) {
  return (
    <RiskMutationToolMessage
      part={part}
      loadingLabel='Unlinking vendor from risk...'
      completedLabel='Vendor unlinked from risk'
      detailFields={['riskId', 'vendorId']}
    />
  )
}

export function RisksUpdateToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const failed = state.isError || isToolError(state)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Updating risk...'
      completedLabel='Risk updated'
      loadingDetail={pickString(state.args?.riskId)}
      completedDetail={failed ? formatChangedFields(state) : null}
      completedIcon={completedIcon}
    />
  )
}
